/**
 * Paiement en plusieurs fois — petits calculs d'affichage partagés par le
 * bloc de choix et les boutons « Payer » des trois tunnels (adhésion,
 * billetterie, boutique). Les montants viennent d'`echeancierDuPanier`
 * (@donoor/utils), au centime de ce que le serveur prélèvera.
 */
import { echeancierDuPanier, jourCivilParis } from "@donoor/utils";

export interface PartDuPanier {
  numero: number;
  date: string;
  montant: number;
}

/**
 * Les parts SUIVANTES (après celle d'aujourd'hui) si elles ont toutes le même
 * montant — « puis 2 × 20 € ». `null` sinon (centime en trop, frais…) : on
 * dit alors seulement combien de prélèvements suivent.
 */
export function suiteIdentique(parts: PartDuPanier[]): { nombre: number; montant: number } | null {
  const suite = parts.slice(1);
  if (!suite.length) return null;
  const montant = suite[0]!.montant;
  return suite.every((p) => Math.abs(p.montant - montant) < 0.005) ? { nombre: suite.length, montant } : null;
}

/** Total payé sur toutes les parts (euros). */
export function totalDesParts(parts: PartDuPanier[]): number {
  return Math.round(parts.reduce((s, p) => s + p.montant, 0) * 100) / 100;
}

/** Parts du panier pour le nombre choisi (vide en une fois) — `echeancierDuPanier`, calcul du serveur. */
export function partsDuPanier(params: {
  nombre: number | null | undefined;
  montantDiviseEuros: number;
  enPlusEuros: number;
  soutienEuros: number;
  tauxCommission: number;
  fraisCouverts: boolean;
}): PartDuPanier[] {
  const n = params.nombre ?? 0;
  if (n < 2) return [];
  return echeancierDuPanier({
    montantDiviseEuros: params.montantDiviseEuros,
    nombre: n,
    premierJour: jourCivilParis(),
    enPlusEuros: params.enPlusEuros,
    soutienEuros: params.soutienEuros,
    tauxCommission: params.tauxCommission,
    fraisCouverts: params.fraisCouverts,
  });
}

/**
 * Frais couverts sur TOUTE la commande en plusieurs fois : chaque paiement a
 * son frais fixe, le total diffère donc de celui d'un paiement en une fois.
 */
export function fraisDesParts(parts: PartDuPanier[], horsFraisEuros: number): number {
  return Math.max(0, Math.round((totalDesParts(parts) - horsFraisEuros) * 100) / 100);
}
